export interface MedikeiAllergy {
  id: string;
  allergen: string;
  severity?: string | null;
  reaction?: string | null;
  memedId?: number | null;
}

export interface MemedAllergyPayload {
  allergy: string;
  type: 'intolerance' | 'allergy' | 'reaction';
}

export interface SetMemedAllergiesResult {
  success: boolean;
  allergiesSent: number;
  errors: string[];
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function mapSeverityToType(severity?: string | null, reaction?: string | null): 'intolerance' | 'allergy' | 'reaction' {
  if (!severity && !reaction) {
    return 'allergy';
  }

  const normalizedSeverity = normalizeText(severity || '');
  const normalizedReaction = normalizeText(reaction || '');

  if (normalizedSeverity.includes('intoler') || normalizedReaction.includes('intoler')) {
    return 'intolerance';
  }

  if (normalizedSeverity.includes('reacao') || normalizedReaction.includes('reacao') || normalizedSeverity.includes('anafilax') || normalizedReaction.includes('anafilax')) {
    return 'reaction';
  }

  return 'allergy';
}

export function mapAllergyToMemed(allergy: MedikeiAllergy): MemedAllergyPayload {
  return {
    allergy: allergy.allergen,
    type: mapSeverityToType(allergy.severity, allergy.reaction),
  };
}

export function mapAllergiesToMemed(allergies: MedikeiAllergy[]): MemedAllergyPayload[] {
  return allergies
    .filter((allergy) => {
      return allergy.allergen && allergy.allergen.trim() !== '';
    })
    .map((allergy) => mapAllergyToMemed(allergy));
}

export function mapAllergiesToMemedIds(
  allergies: Array<{ allergen: string; memedId?: number | null }>
): { ids: number[]; unmapped: string[] } {
  const ids: number[] = [];
  const unmapped: string[] = [];

  for (const allergy of allergies) {
    const allergen = allergy.allergen?.trim();
    if (!allergen) {
      continue;
    }

    if (typeof allergy.memedId === 'number' && Number.isFinite(allergy.memedId) && allergy.memedId > 0) {
      ids.push(allergy.memedId);
    } else {
      unmapped.push(allergen);
    }
  }

  return { ids, unmapped };
}

export function validateMemedAllergy(allergy: MemedAllergyPayload): { valid: boolean; error?: string } {
  if (!allergy.allergy || allergy.allergy.trim() === '') {
    return { valid: false, error: 'Nome da alergia é obrigatório' };
  }

  const validTypes = ['intolerance', 'allergy', 'reaction'];
  if (!validTypes.includes(allergy.type)) {
    return { valid: false, error: `Tipo de alergia inválido: ${allergy.type}` };
  }

  return { valid: true };
}
